import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "overlay", "dialog", "title", "subtitle", "list", "emptyState"]
  static values = { url: String }

  async open(event) {
    event.preventDefault()
    event.stopPropagation()

    const date = event.currentTarget.dataset.date
    const time = event.currentTarget.dataset.time

    if (!date || !time) return

    // Open modal immediately so user sees feedback
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this.titleTarget.textContent = date
    this.subtitleTarget.textContent = time
    this.listTarget.innerHTML = "<p class='py-4 text-center text-sm text-gray-500'>Cargando…</p>"
    this.listTarget.classList.remove("hidden")
    this.emptyStateTarget.classList.add("hidden")

    const url = `${this.urlValue}?date=${encodeURIComponent(date)}&time=${encodeURIComponent(time)}`
    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      const data = await response.json()

      this.titleTarget.textContent = data.date_label || date
      this.subtitleTarget.textContent = data.time_label || time
      this.listTarget.innerHTML = ""

      const registered = data.registered || []
      const suspended = data.suspended || []
      const rescheduled = data.rescheduled || []
      const trial = data.trial || []
      const hasAny = registered.length > 0 || suspended.length > 0 || rescheduled.length > 0 || trial.length > 0

      if (hasAny) {
        this.emptyStateTarget.classList.add("hidden")
        this.listTarget.classList.remove("hidden")
        if (registered.length > 0) this.listTarget.appendChild(this.buildSection(data.registered_title, registered))
        if (trial.length > 0) this.listTarget.appendChild(this.buildSection(data.trial_title, trial, "trial"))
        if (suspended.length > 0) this.listTarget.appendChild(this.buildSection(data.suspended_title, suspended))
        if (rescheduled.length > 0) this.listTarget.appendChild(this.buildSection(data.rescheduled_title, rescheduled))
      } else {
        this.listTarget.classList.add("hidden")
        this.emptyStateTarget.classList.remove("hidden")
      }
    } catch (err) {
      this.listTarget.innerHTML = "<p class='py-4 text-center text-sm text-red-600'>Error al cargar la lista.</p>"
      this.listTarget.classList.remove("hidden")
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  buildSection(title, users, variant = "default") {
    const section = document.createElement("div")
    section.className = "mb-4 last:mb-0"
    const heading = document.createElement("p")
    heading.className = "mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500"
    heading.textContent = title + ":"
    section.appendChild(heading)
    const list = document.createElement("div")
    list.className = "space-y-1.5"

    const avatarClasses = variant === "trial"
      ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"
      : "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700"

    users.forEach(user => {
      const item = document.createElement("div")
      item.className = "flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2"
      const badges = Array.isArray(user.badges) ? user.badges : []
      const badgesHtml = badges.length > 0
        ? `<div class="mt-1 flex flex-wrap gap-1">${badges.map(b => this.badgeHtml(b)).join("")}</div>`
        : ""
      item.innerHTML = `
        <div class="${avatarClasses}">
          <span class="text-xs font-bold">${this.escapeHtml(user.initials || "")}</span>
        </div>
        <div class="min-w-0">
          <p class="truncate text-sm font-medium text-gray-900">${this.escapeHtml(user.name || "")}</p>
          <p class="truncate text-xs text-gray-500">${this.escapeHtml(user.phone || "")}</p>
          ${badgesHtml}
        </div>
      `
      list.appendChild(item)
    })
    section.appendChild(list)
    return section
  }

  badgeHtml(text) {
    const label = this.escapeHtml(text || "")
    // Currently only used for "Reprogramado" in the registered list.
    return `<span class="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700 ring-1 ring-inset ring-orange-600/20">${label}</span>`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  closeOnContainer(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }
}
